import { t } from "i18next";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";

import Icons from "@app/assets/Icons";
import InactiveSubmission from "@app/components/InactiveSubmission";
import Loader from "@app/components/Loader";
import Pagination from "@app/components/Pagination";
import TableHeader from "@app/components/TableHeader";
import ViewUtilityHeader from "@app/components/ViewUtilityHeader";
import { RouteNames } from "@app/routes/index";
import { getProducts } from "@app/services/Endpoints/Product.API";
import { HeadersMapInterface } from "@app/types/Common.types";
import { Products } from "@app/types/Service.type";
import { AppContext } from "@app/utils/contexts/App.context";
import { FilterContext } from "@app/utils/contexts/Filters.context";

import "./index.scss";

let totalPages = 0;
const sortByEnum = {
    DESC: 1,
    ASC: 0,
};

const ViewProductPage: FC = () => {
    const { userPermissions } = useContext(AppContext);
    const { statusesFilter } = useContext(FilterContext);
    const HeadersMap: HeadersMapInterface = {
        serialNo: {
            label: t("VIEW.PRODUCTS.SERIALNO"),
            layout: { lg: 2, sm: 2 },
            disableSort: true,
        },
        name: { label: t("VIEW.PRODUCTS.NAME"), layout: { lg: 2, sm: 2 } },
        displayName: { label: t("VIEW.PRODUCTS.DISPLAYNAME"), layout: { lg: 2, sm: 2 } },
        code: { label: t("VIEW.PRODUCTS.ID"), layout: { lg: 3, sm: 3 } },
        status: { label: t("VIEW.PRODUCTS.STATUS"), layout: { lg: 2, sm: 2 } },
    };
    const [products, setProducts] = useState<Products[]>([]);
    const [sortByObj, setSortByObj] = useState({ sortBy: "name", sortOrder: sortByEnum.ASC });
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [loader, setLoader] = useState<boolean>(false);
    const [searchVal, setSearchVal] = useState<string>("");
    const [isScrollComing, setIsScrollComing] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ref = useRef<any>();
    const navigate = useNavigate();
    const { state } = useLocation();

    const itemId = state?.itemId;
    const itemName = state?.itemName;

    useEffect(() => {
        setPageNumber(1);
    }, [statusesFilter, itemId]);

    useEffect(() => {
        const filters = [
            statusesFilter?.map((status) => `&Status=${status}`).join(""),
            itemId && `ItemId=${itemId}`,
        ].join("&");

        getProductsHandler(
            sortByObj.sortBy,
            sortByObj.sortOrder,
            pageNumber,
            pageSize,
            searchVal,
            filters,
        );
    }, [pageNumber, pageSize, sortByObj, searchVal, statusesFilter, itemId]);

    useEffect(() => {
        if (ref.current?.offsetWidth != ref.current?.clientWidth) {
            setIsScrollComing(true);
        } else {
            setIsScrollComing(false);
        }
    });

    const getProductsHandler = (
        sortBy: string,
        sortOrder: number,
        pageNumber: number,
        pageSize: number,
        name?: string,
        filters?: string,
    ) => {
        setLoader(true);
        getProducts(sortBy, sortOrder, pageNumber, pageSize, name, undefined, filters)
            .then((res) => {
                totalPages = res.data.totalPages;
                const result = res?.data?.data;
                if (result) {
                    setProducts([...result]);
                } else {
                    setProducts([]);
                }
            })
            .catch((err) => console.error(err))
            .finally(() => setLoader(false));
    };

    const sortByHandler = (headerKey: string) => {
        setSortByObj({ sortBy: headerKey, sortOrder: Number(!sortByObj.sortOrder) });
    };

    const onChangeRecordsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = event.target;
        setPageSize(Number(value));
        setPageNumber(1);
    };

    const onPageChange = (pageNumber: number) => {
        setPageNumber(Number(pageNumber));
    };
    const onEdit = (productId: string) => {
        navigate(RouteNames.Edit, {
            state: { productEditData: productId },
        });
    };
    const redirectToAddProduct = () => {
        navigate(RouteNames.Add);
    };
    const handleSearch = (val: string) => {
        setSearchVal(val);
    };

    return (
        <Container fluid className='product'>
            <ViewUtilityHeader
                onChange={onChangeRecordsPerPage}
                placeholder='Search for Product name'
                btnLabel={t("VIEW.PRODUCTS.ADDPRODUCT")}
                onClick={userPermissions?.Create_Product ? redirectToAddProduct : undefined}
                onSearch={handleSearch}
            />

            {itemName && itemId && (
                <InactiveSubmission title='Products for Items:' subTitle={itemName} />
            )}

            <TableHeader
                headersMap={HeadersMap}
                sortByHandler={sortByHandler}
                sortByObj={sortByObj}
                enableIconColumn
            />

            <div
                className={`product--page ${isScrollComing ? "product--isScroll" : ""}`}
                id='listing-Outercontainer'
                ref={ref}
            >
                {loader ? (
                    <Loader />
                ) : products.length ? (
                    products.map((item, index) => (
                        <Row
                            key={index}
                            className={`product__rowWrap ${index % 2 == 0 && "product__rowGrey"}`}
                        >
                            <Col className='product--pl20' sm='2' lg='2'>
                                {pageNumber == 1 && index < 9 ? "0" : ""}
                                {(pageNumber - 1) * 10 + (index + 1)}
                            </Col>
                            <Col sm='2' lg='2'>
                                {item.name}
                            </Col>

                            <Col sm='2' lg='2'>
                                {item.displayName}
                            </Col>
                            <Col sm='3' lg='3'>
                                {item.code}
                            </Col>
                            <Col sm='2' lg='2'>
                                {item.status
                                    ? t("VIEW.PRODUCTS.ACTIVE")
                                    : t("VIEW.PRODUCTS.INACTIVE")}
                            </Col>
                            <Col sm='2' lg='1'>
                                {userPermissions?.Edit_Product && (
                                    <img
                                        className='product__enablepointer'
                                        src={Icons.EditIcon}
                                        onClick={() => onEdit(item.id)}
                                        height='20px'
                                    />
                                )}
                            </Col>
                        </Row>
                    ))
                ) : (
                    <div className='customisation--noData'>{t("APIMSG.NODATA")}</div>
                )}
            </div>
            <Pagination
                sortByObj={sortByObj}
                onPageChange={onPageChange}
                totalPages={totalPages}
                pageSize={pageSize}
                pageNumber={pageNumber}
            />
        </Container>
    );
};

export default ViewProductPage;
